/**
 * Derivations for the Orbit "Intake and billing" patient left-panel section.
 *
 * The Consult row's status — not scheduled, scheduled, occurred, no-show or
 * cancelled — comes from `callRowStatus`.
 *
 * This reads `Appointment.status` directly and is therefore unaffected by the
 * `bariatric-procedure-stage` tag, which lags an appointment's status until a
 * bot recomputes it. Do not reach for the stage tag here.
 */

import { Appointment } from '@medplum/fhirtypes';
import { CONSULTATION_APPOINTMENT_CODES } from './constants';

/** Statuses that mean the appointment never counted — neither held nor missed. */
const IGNORED_STATUSES: Appointment['status'][] = ['cancelled', 'entered-in-error'];

export type CallRowStatus =
  | { kind: 'none' }
  | { kind: 'scheduled'; date: string }
  | { kind: 'occurred'; date: string }
  | { kind: 'noshow'; date: string }
  | { kind: 'cancelled'; date: string };

/**
 * When the appointment sits on the timeline. `start` is authoritative; `end` is
 * the fallback for the rare appointment carrying only a finish time.
 */
function appointmentDate(appointment: Appointment): string | undefined {
  return appointment.start ?? appointment.end;
}

function isLive(appointment: Appointment): boolean {
  if (appointment.status && IGNORED_STATUSES.includes(appointment.status)) return false;
  return appointmentDate(appointment) !== undefined;
}

function timeOf(appointment: Appointment): number {
  return new Date(appointmentDate(appointment) as string).getTime();
}

/**
 * Collapses a patient's appointments of one kind into the single status the
 * panel row renders.
 *
 * An upcoming booking wins over history — "where is this patient" is answered by
 * the next thing on the calendar, not the last thing that happened. Absent one,
 * the most recent past appointment is reported, and a past appointment still
 * marked `booked` reads as **occurred**: "Re-Scheduled" has no distinct FHIR
 * status, so a booking whose time has passed is either attended-but-unmarked or
 * abandoned, and both are history rather than something still to come.
 *
 * `noshow` is never treated as upcoming — it is a recorded outcome.
 */
export function callRowStatus(appointments: Appointment[], now: Date): CallRowStatus {
  const live = appointments.filter(isLive);
  if (live.length === 0) return latestCancelled(appointments);

  const nowMs = now.getTime();
  const upcoming = live
    .filter((a) => a.status !== 'noshow' && a.status !== 'fulfilled' && timeOf(a) > nowMs)
    .sort((a, b) => timeOf(a) - timeOf(b));

  if (upcoming.length > 0) {
    return { kind: 'scheduled', date: appointmentDate(upcoming[0]) as string };
  }

  const latest = [...live].sort((a, b) => timeOf(b) - timeOf(a))[0];
  const date = appointmentDate(latest) as string;

  return latest.status === 'noshow' ? { kind: 'noshow', date } : { kind: 'occurred', date };
}

/**
 * Every appointment of the kind was cancelled: the latest one, so the row says what happened to the
 * consult rather than reading as though it was never booked.
 */
function latestCancelled(appointments: Appointment[]): CallRowStatus {
  const cancelled = appointments
    .filter((a) => a.status === 'cancelled' && appointmentDate(a) !== undefined)
    .sort((a, b) => timeOf(b) - timeOf(a))[0];

  return cancelled ? { kind: 'cancelled', date: appointmentDate(cancelled) as string } : { kind: 'none' };
}

/**
 * The provider consultation.
 *
 * Mirrors the bots-side `getPatientConsultations()` in
 * `packages/bots/src/services/procedure-stage.ts`, which `provider-app` cannot
 * import. Both read the same `CONSULTATION_APPOINTMENT_CODES`.
 */
export function getConsultAppointments(appointments: Appointment[]): Appointment[] {
  const codes = CONSULTATION_APPOINTMENT_CODES as readonly string[];

  return appointments.filter((appointment) =>
    appointment.appointmentType?.coding?.some((coding) => coding.code !== undefined && codes.includes(coding.code))
  );
}
